// Ambient background audio engine using Web Audio API.
// Generates soundscapes algorithmically — no external assets required.

let ctx = null;
let activeNodes = [];
let masterGain = null;

const getCtx = () => {
  if (!ctx) {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    ctx = new AudioCtx();
  }
  return ctx;
};

const stop = () => {
  activeNodes.forEach((n) => {
    try {
      if (typeof n.stop === "function") n.stop(0);
      n.disconnect();
    } catch (e) {}
  });
  activeNodes = [];
  if (masterGain) {
    try {
      masterGain.disconnect();
    } catch (e) {}
    masterGain = null;
  }
};

const createNoiseBuffer = (audioCtx, type = "pink") => {
  const bufferSize = 2 * audioCtx.sampleRate;
  const noiseBuffer = audioCtx.createBuffer(2, bufferSize, audioCtx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const output = noiseBuffer.getChannelData(ch);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      if (type === "pink") {
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        output[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
        b6 = white * 0.115926;
      } else {
        output[i] = white * 0.3;
      }
    }
  }
  return noiseBuffer;
};

const startBinaural = (leftHz, rightHz, volume = 0.15) => {
  const c = getCtx();
  masterGain = c.createGain();
  masterGain.gain.value = volume;
  masterGain.connect(c.destination);

  const merger = c.createChannelMerger(2);
  merger.connect(masterGain);

  const oscL = c.createOscillator();
  oscL.frequency.value = leftHz;
  oscL.type = "sine";
  oscL.connect(merger, 0, 0);
  oscL.start();

  const oscR = c.createOscillator();
  oscR.frequency.value = rightHz;
  oscR.type = "sine";
  oscR.connect(merger, 0, 1);
  oscR.start();

  activeNodes = [oscL, oscR, merger];
};

const startNoise = (type, volume = 0.08) => {
  const c = getCtx();
  masterGain = c.createGain();
  masterGain.gain.value = volume;
  masterGain.connect(c.destination);

  const src = c.createBufferSource();
  src.buffer = createNoiseBuffer(c, type);
  src.loop = true;

  // Lowpass for warmth
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = type === "ocean" ? 600 : 2000;

  src.connect(filter).connect(masterGain);
  src.start();

  // Slow modulation for ocean
  if (type === "ocean") {
    const lfo = c.createOscillator();
    const lfoGain = c.createGain();
    lfo.frequency.value = 0.15;
    lfoGain.gain.value = 300;
    lfo.connect(lfoGain).connect(filter.frequency);
    lfo.start();
    activeNodes.push(lfo);
  }

  activeNodes.push(src, filter);
};

const startDrone = (volume = 0.1) => {
  const c = getCtx();
  masterGain = c.createGain();
  masterGain.gain.value = volume;
  masterGain.connect(c.destination);

  [110, 165, 220].forEach((f) => {
    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.value = f;
    const g = c.createGain();
    g.gain.value = 0.4;
    osc.connect(g).connect(masterGain);
    osc.start();
    activeNodes.push(osc, g);
  });
};

export const AMBIENT_OPTIONS = [
  { value: "none", label: "None" },
  { value: "binaural432", label: "Binaural 432Hz (Calm)" },
  { value: "binaural528", label: "Binaural 528Hz (Healing)" },
  { value: "ocean", label: "Ocean Waves" },
  { value: "pink", label: "Pink Noise" },
  { value: "drone", label: "Deep Drone" },
];

export const startAmbient = (kind) => {
  stop();
  if (!kind || kind === "none") return;
  // Resume context (browser auto-suspends until user gesture)
  const c = getCtx();
  if (c.state === "suspended") c.resume();

  if (kind === "binaural432") startBinaural(432, 440);
  else if (kind === "binaural528") startBinaural(528, 536);
  else if (kind === "ocean") startNoise("ocean", 0.12);
  else if (kind === "pink") startNoise("pink", 0.06);
  else if (kind === "drone") startDrone();
};

export const stopAmbient = () => stop();

export const setAmbientVolume = (vol) => {
  if (masterGain) masterGain.gain.value = vol;
};
